import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, GripVertical, Layers } from "lucide-react";
import { useState } from "react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  FilterBar,
  PageHeader,
  Select,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  PageResponse,
  Priority,
  SprintSummary,
  WorkItemSummary,
} from "../../shared/types/api";
import { WorkItemTypeBadge } from "../workitems/WorkItemTypeBadge";
import {
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  PRIORITY_TONE,
  formatHours,
} from "../workitems/workItemVocabulary";
import { BulkUpdateDrawer } from "./BulkUpdateDrawer";
import { CarryoverBanner } from "./CarryoverBanner";
import { useProjectSelection } from "./useProjectSelection";
import { usePublishCurrentProject } from "../../shared/hooks/currentProject";

/**
 * The product backlog (§47).
 *
 * <p>Order is the content here, not decoration: the backlog <em>is</em> the answer to "what do we
 * build next". So dragging is supported, and so are keyboard-reachable up and down buttons —
 * ordering a list is exactly the interaction that becomes impossible without them, and it is the
 * one thing on this page that cannot be done any other way.
 *
 * <p>Each move sends only the item and its two new neighbours. Sending the whole ordered list
 * would mean two people reordering at once overwrite each other's entire backlog rather than one
 * row.
 */
/* The parameters this screen owns; outside the component so useUrlFilters can memoise on it. */
const BACKLOG_FILTERS = { q: "", priority: "" };

export function BacklogPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { projectId, select, projects, loading, selected } = useProjectSelection();

  /* In the URL: opening an item from the backlog and coming back used to land unfiltered. */
  const [filters, setFilters] = useUrlFilters(BACKLOG_FILTERS);
  const { q: query } = filters;
  const priority = filters.priority as Priority | "";
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const canRank = can("BACKLOG_RANK");

  usePublishCurrentProject(projectId, selected?.name);

  const backlog = useQuery({
    queryKey: ["backlog", projectId, priority, query],
    queryFn: () => {
      const params = new URLSearchParams({ projectId, size: "200" });
      if (priority) {
        params.set("priority", priority);
      }
      if (query.trim()) {
        params.set("q", query.trim());
      }
      return api.get<PageResponse<WorkItemSummary>>(`/api/v1/backlog?${params}`);
    },
    enabled: Boolean(projectId),
  });

  const sprints = useQuery({
    queryKey: ["sprints", projectId],
    queryFn: () => api.get<SprintSummary[]>(`/api/v1/sprints?projectId=${projectId}`),
    enabled: Boolean(projectId),
  });

  const items = backlog.data?.content ?? [];
  const openSprints = (sprints.data ?? []).filter(
    (sprint) => sprint.status === "PLANNED" || sprint.status === "ACTIVE",
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["backlog", projectId] });
    void queryClient.invalidateQueries({ queryKey: ["sprints", projectId] });
  };

  const move = async (workItemId: string, afterId: string | null, beforeId: string | null) => {
    setError(null);
    try {
      await api.post("/api/v1/backlog/rank", { workItemId, afterId, beforeId });
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reorder the backlog.");
    }
  };

  /**
   * Places an item at a position, expressed as the two items it lands between.
   *
   * <p>Which neighbours those are depends on the direction of travel: moving down, the item at
   * the target index slides up to make room, so it becomes the one above; moving up, it becomes
   * the one below. Getting this backwards puts the item one place from where it was dropped,
   * which looks like the drag not quite working.
   */
  const placeAt = (workItemId: string, from: number, to: number) => {
    const movingDown = from < to;
    const afterId = movingDown ? (items[to]?.id ?? null) : (items[to - 1]?.id ?? null);
    const beforeId = movingDown ? (items[to + 1]?.id ?? null) : (items[to]?.id ?? null);
    void move(workItemId, afterId, beforeId);
  };

  /** Moves an item one place, which is what the keyboard-reachable buttons do. */
  const nudge = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    const item = items[index];
    if (!item || target < 0 || target >= items.length) {
      return;
    }
    placeAt(item.id, index, target);
  };

  const dropOn = (index: number) => {
    if (!dragging) {
      return;
    }
    const from = items.findIndex((item) => item.id === dragging);
    const id = dragging;
    setDragging(null);
    if (from === -1 || from === index) {
      return;
    }
    placeAt(id, from, index);
  };

  const moveToSprint = async (sprintId: string | null) => {
    setError(null);
    try {
      await api.post("/api/v1/backlog/move-to-sprint", {
        workItemIds: [...chosen],
        sprintId,
      });
      setChosen(new Set());
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not move that work.");
    }
  };

  const toggle = (id: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) {
    return <LoadingState label="Loading projects…" />;
  }
  if (projects.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No projects yet"
          message="A backlog belongs to a project. Create one first."
          action={<Button onClick={() => navigate("/projects")}>Go to projects</Button>}
        />
      </Card>
    );
  }

  const chosenPoints = items
    .filter((item) => chosen.has(item.id))
    .reduce((total, item) => total + (item.storyPoints ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Backlog"
        subtitle="What gets built next, in order. Everything here is open work in no sprint."
      />

      {error && (
        <div className="mb-4">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      <FilterBar actions={
          <div className="w-64">
            {/* Named, because it is the only control on the page with no visible label and it
                decides what every row below it is. */}
            <Select
              aria-label="Project"
              value={projectId}
              onChange={(event) => select(event.target.value)}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </div>
        }>
          <div className="min-w-56 flex-1">
            <Field label="Search">
              <Input
                placeholder="Title or key"
                value={query}
                onChange={(event) => setFilters({ q: event.target.value })}
              />
            </Field>
          </div>
          {/* Narrowed on the server, like the search beside it. The rows that survive keep the
              backlog's own order — this says which part of the list to look at, never what the
              order is. Dragging still ranks against the neighbours on screen, which is what the
              search box has always done. */}
          <div className="w-44">
            <Field label="Priority">
              <Select
                value={priority}
                onChange={(event) => {
                  setFilters({ priority: event.target.value });
                  /* A selection made in one view means nothing in another: the items it named
                     may not be on screen to act on any more. */
                  setChosen(new Set());
                }}
              >
                <option value="">All priorities</option>
                {PRIORITY_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {chosen.size > 0 ? (
            <>
              <div className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700">
                <Layers className="h-4 w-4" />
                {chosen.size} selected · {chosenPoints} points
              </div>
              {canRank && openSprints.length > 0 && (
                <div className="w-56">
                  <Field label="Move to sprint">
                    <Select
                      value=""
                      onChange={(event) =>
                        event.target.value && void moveToSprint(event.target.value)
                      }
                    >
                      <option value="">Choose a sprint</option>
                      {openSprints.map((sprint) => (
                        <option key={sprint.id} value={sprint.id}>
                          {sprint.name}
                          {sprint.status === "ACTIVE" ? " (running)" : ""}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              )}
              {can("WORK_ITEM_EDIT") && (
                <Button variant="secondary" onClick={() => setBulkOpen(true)}>
                  Edit {chosen.size}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setChosen(new Set())}>
                Clear
              </Button>
            </>
          ) : (
            <p className="py-2 text-sm text-ink-500">
              Select items to move them into a sprint or edit them together.
            </p>
          )}
      </FilterBar>

      {/*
        * What the last sprint left behind, above the list rather than scattered through it.
        * Planning starts from what already went wrong: an item that has been carried twice for
        * the same reason is a different conversation from one nobody has picked up yet.
        */}
      <CarryoverBanner projectId={projectId} onSelect={(ids) => setChosen(new Set(ids))} />

      {backlog.isError ? (
        <ErrorState error={backlog.error} onRetry={() => void backlog.refetch()} />
      ) : backlog.isPending ? (
        <LoadingState label="Loading the backlog…" />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={query ? "Nothing matches that search" : "The backlog is empty"}
            message={
              query
                ? "Try a broader search — the work may be in a sprint already."
                : `Everything in ${selected?.name ?? "this project"} is either in a sprint or finished. Create work items to fill the backlog.`
            }
          />
        </Card>
      ) : (
        <Card>
          <ul>
            {items.map((item, index) => {
              const isChosen = chosen.has(item.id);
              return (
                <li
                  key={item.id}
                  draggable={canRank}
                  onDragStart={() => setDragging(item.id)}
                  onDragEnd={() => setDragging(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => dropOn(index)}
                  className={cn(
                    "flex items-center gap-3 border-b border-ink-100 px-4 py-2.5 last:border-0",
                    isChosen && "bg-brand-50",
                    dragging === item.id && "opacity-40",
                    canRank && "cursor-grab active:cursor-grabbing",
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 accent-brand-600"
                    checked={isChosen}
                    onChange={() => toggle(item.id)}
                    aria-label={`Select ${item.itemKey}`}
                  />

                  {canRank && (
                    <>
                      <GripVertical
                        className="h-4 w-4 shrink-0 text-ink-300"
                        aria-hidden
                      />
                      {/* Buttons as well as dragging: reordering is the one thing on this page
                          that cannot be done any other way, so it must not need a mouse. */}
                      <span className="flex shrink-0 flex-col">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => nudge(index, -1)}
                          aria-label={`Move ${item.itemKey} up`}
                          className="text-ink-400 transition-colors hover:text-brand-600 disabled:opacity-30"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          disabled={index === items.length - 1}
                          onClick={() => nudge(index, 1)}
                          aria-label={`Move ${item.itemKey} down`}
                          className="text-ink-400 transition-colors hover:text-brand-600 disabled:opacity-30"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </span>
                    </>
                  )}

                  <span className="tnum w-8 shrink-0 text-right text-xs text-ink-400">
                    {index + 1}
                  </span>

                  <WorkItemTypeBadge type={item.workItemType} />

                  <button
                    type="button"
                    onClick={() => navigate(`/work-items/${item.itemKey}`)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>
                    <span className="truncate text-sm text-ink-900">{item.title}</span>
                    {/*
                      * Carried work looks exactly like work nobody has tried yet, and a planning
                      * session that cannot tell them apart re-plans the same failure. The reason
                      * rides along in the tooltip so the badge stays one word wide.
                      */}
                    {item.carriedOverFromSprintName && (
                      <span
                        title={
                          item.carriedOverComment
                            ? `${item.carriedOverReason} — ${item.carriedOverComment}`
                            : (item.carriedOverReason ?? undefined)
                        }
                        className="shrink-0"
                      >
                        <StatusBadge tone="amber">
                          Undone from {item.carriedOverFromSprintName}
                        </StatusBadge>
                      </span>
                    )}
                  </button>

                  <StatusBadge tone={PRIORITY_TONE[item.priority]}>
                    {PRIORITY_LABELS[item.priority]}
                  </StatusBadge>

                  <span className="tnum w-16 shrink-0 text-right text-xs text-ink-500">
                    {item.storyPoints != null ? `${item.storyPoints} pts` : "—"}
                  </span>
                  <span className="tnum w-14 shrink-0 text-right text-xs text-ink-500">
                    {formatHours(item.currentEstimateHours)}
                  </span>
                  <span className="w-28 shrink-0 truncate text-xs text-ink-500">
                    {item.assigneeName ?? "Unassigned"}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <BulkUpdateDrawer
        workItemIds={[...chosen]}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => {
          setChosen(new Set());
          refresh();
        }}
      />
    </>
  );
}
